import type { ModuleOptions } from '../../types/module'
// @ts-expect-error virtual import
import { useRuntimeConfig } from '#imports'
import { defu } from 'defu'
import pLimit from 'p-limit'
import { v7 as uuidv7 } from 'uuid'
import { CONFIG_KEY } from '../config'

interface UploadedFileResponse {
  contentType: string
  filename: string
  name: string
  originalFilename: string
  path: string
  size: number
  public: boolean
}

interface UploadFile {
  name?: string
  file: File
}

interface UploadFileOptions {
  onProgress?: (data: { percentage: number }) => void
}

type CreateMultipartUploadResponseRequestMethod = 'GET' | 'HEAD' | 'PATCH' | 'POST' | 'PUT' | 'DELETE' | 'CONNECT' | 'OPTIONS' | 'TRACE'

interface CreateMultipartUploadResponse {
  chunks: {
    request: {
      method: CreateMultipartUploadResponseRequestMethod
      url: string
      headers?: { [key: string]: string | number }
      body?: { [key: string]: string | number }
    }
    start: number
    end: number
    partNumber: number
  }[]
  complete: {
    request: {
      method: CreateMultipartUploadResponseRequestMethod
      url: string
      headers?: HeadersInit
      body?: { [key: string]: string | number }
    }
  }
}

export function useFileUpload() {
  const config = (useRuntimeConfig().public[CONFIG_KEY] as ModuleOptions).supportingService
  async function uploadFile(fileData: UploadFile, options?: UploadFileOptions) {
    const totalSize = fileData.file.size + 2
    let uploadedSize = 0
    function onProgress(loaded: number) {
      if (options?.onProgress) {
        uploadedSize += loaded
        const percentage = (uploadedSize / totalSize) * 100
        options.onProgress({ percentage })
      }
    }
    const { data: createData } = await $fetch<{ data: CreateMultipartUploadResponse }>('/file/upload/multipart/create', {
      baseURL: config.apiUrl,
      method: 'POST',
      body: {
        filename: fileData.file.name,
        contentType: fileData.file.type,
        size: fileData.file.size,
      },
      credentials: 'omit',
    })
    onProgress(1)

    const chunkPromise = pLimit(20)
    const chunkPromises: Promise<{
      partNumber: number
      eTag: string
    }>[] = []
    const multipart = createData.chunks.length > 1
    for (const chunk of createData.chunks) {
      chunkPromises.push(
        chunkPromise(async () => {
          return new Promise((resolve, reject) => {
            let retries = 0
            const maxRetries = 1
            const retryDelay = 0

            function request() {
              const xhr = new XMLHttpRequest()
              xhr.open(chunk.request.method, chunk.request.url)
              xhr.onreadystatechange = function () {
                if (xhr.readyState === 4) {
                  if (xhr.status >= 200 && xhr.status < 300) {
                    resolve({
                      partNumber: chunk.partNumber,
                      eTag: xhr.getResponseHeader('ETag') || '',
                    })
                  }
                  else {
                    if (retries < maxRetries) {
                      retries++
                      setTimeout(request, retryDelay)
                    }
                    else {
                      reject(new Error(`Request failed after ${maxRetries} attempts`))
                    }
                  }
                }
              }

              xhr.onerror = function () {
                if (retries < maxRetries) {
                  retries++
                  setTimeout(request, retryDelay)
                }
                else {
                  reject(new Error(`Request failed after ${maxRetries} attempts`))
                }
              }

              xhr.onabort = () => {
                reject(new Error('Aborted'))
              }

              let lastUploadedSize = 0
              xhr.upload.onprogress = (evt) => {
                if (evt.lengthComputable) {
                  onProgress(evt.loaded - lastUploadedSize)
                  lastUploadedSize = evt.loaded
                }
              }
              if (chunk.request.headers) {
                for (const headerKey in chunk.request.headers) {
                  xhr.setRequestHeader(headerKey, chunk.request.headers[headerKey].toString())
                }
              }

              xhr.send(multipart ? fileData.file.slice(chunk.start, chunk.end) : fileData.file)
            }
            request()
          })
        }),
      )
    }

    const parts = await Promise.all(chunkPromises)
    const { data: completeData } = await $fetch<{ data: UploadedFileResponse }>(createData.complete.request.url, {
      method: createData.complete.request.method,
      body: defu({
        parts,
      }, createData.complete.request.body),
      headers: createData.complete.request.headers,
      credentials: 'omit',
    })
    onProgress(1)
    return {
      ...completeData,
      name: fileData?.name || `file[${uuidv7()}]`,
    }
  }

  function upload(files: UploadFile[], options?: UploadFileOptions) {
    const fileCount = files.length
    let uploadedPercentage = 0
    function onProgress(percentage: number) {
      if (options?.onProgress) {
        uploadedPercentage += percentage
        options?.onProgress({ percentage: uploadedPercentage / fileCount })
      }
    }
    const promises: Promise<UploadedFileResponse>[] = []
    const filePromise = pLimit(5)
    for (const fileName in files) {
      promises.push(
        filePromise(() => {
          let lastPercentage = 0
          return uploadFile(files[fileName], {
            onProgress: (data) => {
              onProgress(data.percentage - lastPercentage)
              lastPercentage = data.percentage
            },
          })
        }),
      )
    }
    return Promise.all(promises)
  }

  return upload
}
